import { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiService from 'api/apiServices';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { generateRandomArticle } from 'components/forms/FormArticleInput';
import { resolveDefaultDimensionId } from 'utils/helpers/catalogDefaults';
import ESpinner from 'components/others/Spinner';
import ServiceFormik from './ServiceFormik';
import { useService } from '../context/ServiceProvider';
import type { ServiceEditType } from '../types';

const emptyService: ServiceEditType = {
    ID: 0,
    servArticle: '',
    servName: '',
    servDescription: '',
    dimensionId: 0,
    servCreated: Date.now(),
    servUpdated: Date.now(),
};

interface OffServiceCardProps {
    fetchServices: (force?: boolean) => Promise<unknown>;
}

const OffServiceCard = ({ fetchServices }: OffServiceCardProps) => {
    const { t } = useTranslation(['Services']);
    const { showEditService, setShowEditService } = useService();
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const [service, setService] = useState<ServiceEditType | null>(null);

    const getCreateDefaults = () => ({
        dimensionId: resolveDefaultDimensionId(localSettings?.DIRS?.Dimension),
    });

    const fetchService = async () => {
        if (showEditService === 0) {
            setService({
                ...emptyService,
                ...getCreateDefaults(),
                servArticle: generateRandomArticle(),
                ownerId: auth.user.ID,
                userName: auth.user.userName,
                userEmail: auth.user.userEmail,
                userPicture: auth.user.userPicture,
            });
        }

        if (typeof showEditService === 'number' && showEditService > 0) {
            const fetched = await apiService.getService(showEditService);
            if (fetched) setService(fetched);
        }

        if (typeof showEditService === 'number' && showEditService < 0) {
            const fetched = await apiService.getService(-showEditService);
            if (fetched) {
                setService({
                    ...fetched,
                    ID: 0,
                    servArticle: generateRandomArticle(),
                    ...getCreateDefaults(),
                });
            }
        }
    };

    const handleClose = () => {
        setShowEditService(false);
        setTimeout(() => {
            setService(null);
        }, 300);
    };

    useEffect(() => {
        if (showEditService !== false) fetchService();
    }, [showEditService]);

    return (
        <Offcanvas show={showEditService !== false} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('titleEdit')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {service ? (
                    <ServiceFormik setService={setService} fetchServices={fetchServices} service={service} />
                ) : (
                    <ESpinner />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffServiceCard;
